/*
  Header search bar — open, close, focus.

  Dawn's <predictive-search> handles querying and rendering results; it does not
  handle revealing the bar, because in stock Dawn the search lives in a modal
  that header.liquid owned. That file is deleted, so this is the small amount of
  behaviour that went with it.

  Deliberately not a custom element: there is exactly one search bar per page and
  nothing else needs to reuse this. Listeners are delegated from document so the
  bar can be re-rendered by the Section Rendering API (the theme editor does this
  on every header setting change) without rebinding.

  `hidden` is the state, not a class — it keeps the bar out of the accessibility
  tree and out of the tab order while closed, which a display:none class would do
  too but without saying so in the markup.
*/
(() => {
  const BAR = '#ctl-search';
  const INPUT = '#ctl-search-input';

  const bar = () => document.querySelector(BAR);

  const isOpen = () => {
    const el = bar();
    return !!el && !el.hidden;
  };

  const open = () => {
    const el = bar();
    if (!el) return;

    /* Below 990px the only search trigger lives inside the nav drawer, so
       opening from there would otherwise leave the drawer sitting behind the
       bar — still holding the scroll lock and still in the tab order. Closing
       it is what a person means by tapping Search. */
    const drawer = document.querySelector('.ctl-drawer[open]');
    if (drawer) drawer.open = false;

    el.hidden = false;
    document.querySelectorAll('[data-ctl-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    /* Focus has to wait a frame: the element is display:none until `hidden`
       clears, and focus() on a still-hidden input is a no-op in every browser. */
    requestAnimationFrame(() => el.querySelector(INPUT)?.focus());
  };

  const close = ({ restoreFocus = true } = {}) => {
    const el = bar();
    if (!el || el.hidden) return;
    el.hidden = true;
    document.querySelectorAll('[data-ctl-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (restoreFocus) document.querySelector('[data-ctl-search-toggle]')?.focus();
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ctl-search-toggle]');
    if (toggle) {
      event.preventDefault();
      isOpen() ? close() : open();
      return;
    }

    if (event.target.closest('[data-ctl-search-close]')) {
      close();
      return;
    }

    /* Clicking away closes it — but not a click inside the bar itself, and not
       a click on a result, which needs to survive long enough to navigate. */
    if (isOpen() && !event.target.closest(BAR)) close({ restoreFocus: false });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isOpen()) return;
    /* Dawn's predictive-search also listens for Escape to dismiss its results.
       Only close the whole bar once the results are already gone, so one press
       clears the suggestions and a second closes the search. */
    const results = document.querySelector('#predictive-search-results');
    if (results) return;
    close();
  });
})();
